// One collection window, from whichever of the two read paths is serving this
// session (#207 U5).
//
// The read-store flag is read in exactly one place, below, so every provider
// takes the same path and flipping the default in `readStoreFlag.js` reaches
// them all. A provider still declares its own collection, `limit()`, ordering
// and normalisation, and renders from its own `useMemo`; it does not declare
// *which store answers*.
//
// ── The query is built here ───────────────────────────────────────────────
//
// Every listener is `col(uid, name)` plus an optional `orderBy` and a
// **mandatory** `limit()` — `firestore.rules` refuses a list without one
// (#64), and that fails at the server, not in a test. Building it here makes
// the `limit` impossible to omit. A provider needing a shape this cannot
// express keeps its own listener and calls `useStoredCollection` directly.
//
// **There is no query engine.** The listener decides the window and the store
// mirrors it. `orderByField` is passed to Firestore and never re-applied
// locally — delivered order is the ordering contract, and `appStore`'s receipt
// carries it.
//
// ── Callers name this `read`, never `window` ─────────────────────────────
//
// `const window = useCollectionWindow(…)` would shadow the global for the rest
// of the function, so a later `window.localStorage` would silently get a
// collection read.
//
// ── `includeMetadataChanges` ──────────────────────────────────────────────
//
// On the store path `pendingWrite` comes from **our** queue through the
// overlay. On the snapshot path (the rollback) a provider's `withMetadata`
// keeps the SDK's `hasPendingWrites` callbacks.
//
// **Both listeners register with the option (#651)**, and pass on only what
// the provider would have had without it, plus the change from the cache's
// answer to the server's — `deliverable`, below. That one snapshot is how a
// device that answered from an empty cache offline learns that the server has
// spoken.
//
// On the snapshot arm `pendingWrite` is the SDK's mutation queue, which lite
// dispatch (#410) would empty. `src/data/liteWriteFlag.js` refuses lite writes
// whenever the read-store flag says `'off'`, so this arm always writes through
// `firebase/firestore`. `test/ui/rollbackArmPendingWrite.test.jsx` holds each
// arm to its supplier.

import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { onSnapshot, query, limit as limitTo, orderBy } from 'firebase/firestore';
import { col } from './paths';
import { READ_STATE, collectionScope } from './appStore';
import { forgetRead, reportRead } from './staleReads';
import {
  forgetAwaiting, rememberServerAnswered, reportAwaiting, serverAnsweredBefore,
} from './serverAnswers';
import { answerIsRead } from '../domain/reading';
import { isConfigured } from '../lib/firebase';
import { theReadPath } from './readStore';
import { readStoreEnabled } from './readStoreFlag';
import { useStoredCollection } from './useStoredCollection';
import { onRetryReads } from './readRetry';

/**
 * What a provider gets when it does not normalise: the document spread flat
 * with its two carriage flags, `pendingWrite` and `partial` (#268), beside it.
 */
const spreadRow = ({ id, data, pendingWrite, partial }) => ({
  id, ...data, pendingWrite, partial,
});

// ── The two flags never reach a stored document ───────────────────────────
//
// If a consumer handed a read row back to a write, `partial` and
// `pendingWrite` would become stored fields. **No provider write reaches a
// document from a read row.** Every writer in `src/data/` either
//
//   * destructures its argument by name — `saveCategory({ id, label, color,
//     billable, reminderLeadHours })`, `saveTag`, `addSession`, `addEntry`,
//     `saveTemplate`, `addPiece`, `createReport`, `setAttendance` — so an extra
//     key has nowhere to go; or
//   * normalises it — `createStudent` through `normaliseStudent`, `saveStudio`
//     through `normaliseStudio`; or
//   * spreads a **patch** (`updateStudent`, `updateFamily`, `updateSeries`,
//     `updateEntry`), and every caller of those four builds its patch from form
//     state through `normaliseStudentPatch`, `normaliseFamilyPatch`,
//     `fieldsFromForm` or the expense form's own `payload` — never from a row
//     this hook returned.
//
// The call sites that spread into a writer — `saveCategory({ ...editing,
// label })` and `addSession({ studentId, ...timeForm })` — both land on a
// destructuring writer. The four patch-spreading writers above are the ones a
// new caller could break this through.

/** One frozen empty list, so a signed-out render is referentially stable. */
const EMPTY = [];

// ── Every listener hears metadata changes, and passes on only some (#651) ──
//
// Whether an answer came from the server is `metadata.fromCache`, and the
// moment that matters — a device that answered from an empty cache offline
// reaching the server — can change nothing else: a studio that really has no
// invoices answers `[]` from the cache and `[]` from the server. Firestore
// raises that change only to a listener registered with
// `includeMetadataChanges`, so every listener here is, and this decides which
// snapshots go on: the first; any whose documents changed, which is every
// snapshot a listener without the option would have been given; and any whose
// `fromCache` moved. The `hasPendingWrites` flips the option also brings are
// dropped, so a provider that did not ask for them sees what it saw before.
//
// A snapshot that does not carry `metadata` or `docChanges` — a test's stand-in
// — is passed on, and counted as the server's.

/** The snapshot's `fromCache`, false when it does not say. */
const cached = (snap) => Boolean(snap && snap.metadata && snap.metadata.fromCache === true);

/** Whether to pass `snap` on, given the `fromCache` of the last one passed (null: none yet). */
function deliverable(previousFromCache, snap) {
  if (previousFromCache === null) return true;
  if (cached(snap) !== previousFromCache) return true;
  return typeof snap.docChanges !== 'function' || snap.docChanges().length > 0;
}

/** A distinct key per mounted window, for the awaiting registry. */
let windowSerial = 0;

/**
 * @param uid           the studio, or falsy for signed out.
 * @param collection    a `COLLECTIONS` name.
 * @param limit         the mandatory `limit()` (#64).
 * @param orderByField  a field name for Firestore's `orderBy`, or null.
 * @param withMetadata  `includeMetadataChanges` on the **snapshot** path only.
 * @param normalise     `({ id, data, pendingWrite, partial }) => row`.
 *
 * Returns `{ docs, hasRead, hasAnswer, loading, failed, awaiting, readVia,
 * readState, readSeenAt, complete, refusals, storeRefusals, error }`.
 *
 * `hasRead` is *the server has answered for this scope*; `hasAnswer` is *there
 * is something to show*. They are different questions and both are offered by
 * name — see `useDocumentWindow`'s header, where #279 sets out what it cost to
 * have only one of them.
 *
 * `readVia` is `'store'` or `'snapshot'`. `readState` and `readSeenAt` are
 * `null` on the snapshot path, as they always were. The sync chip's stale
 * count is reported from both paths (#682) — see `reportRead` below.
 *
 * **A stale read is marked, never blocked** (#207 Q5). This returns `readState`
 * untouched and refuses nothing.
 */
export function useCollectionWindow({
  uid,
  collection,
  limit,
  orderByField = null,
  withMetadata = false,
  normalise = spreadRow,
}) {
  // Read once per mount and never per render — see `readStoreFlag.js`. A value
  // that changed between renders would change which of two subscriptions this
  // holds while it holds one of them.
  const [viaStore] = useState(() => readStoreEnabled());
  const readPath = useMemo(() => (viaStore ? theReadPath() : null), [viaStore]);

  const studio = (isConfigured && uid) || null;

  // Try again (#647): a new attempt is a new listener. Bumped by
  // `retryFailedReads` (src/data/readRetry.js), and only while this window's
  // read has failed — `failedRef` is set at the foot of the hook.
  const [attempt, setAttempt] = useState(0);
  const failedRef = useRef(false);
  useEffect(() => onRetryReads(() => {
    if (failedRef.current) setAttempt((a) => a + 1);
  }), []);

  const buildQuery = useCallback(() => {
    const parts = [];
    if (orderByField) parts.push(orderBy(orderByField));
    parts.push(limitTo(limit));
    return query(col(studio, collection), ...parts);
  }, [studio, collection, orderByField, limit]);

  // The store path's seam. No `includeMetadataChanges` for `pendingWrite`: the
  // pending flag comes from our queue.
  //
  // `onError` is the listener's error (#647). Without it the SDK logs the
  // error, stops the listener and tells nobody, and the window waits for an
  // answer that is not coming. `attempt` is in the dependencies so that a
  // retry hands `useStoredCollection` a new subscriber, which it subscribes.
  //
  // `includeMetadataChanges` (#651), filtered by `deliverable` so the store
  // is written no more often than before, plus the one change it needs: the
  // server's first answer, which the store records as `confirmed`.
  const subscribe = useCallback((onWindow, onError) => {
    if (!studio) return () => {};
    let lastFromCache = null;
    return onSnapshot(buildQuery(), { includeMetadataChanges: true }, (snap) => {
      if (!deliverable(lastFromCache, snap)) return;
      lastFromCache = cached(snap);
      onWindow(
        snap.docs.map((d) => ({ id: d.id, data: d.data() })),
        { fromCache: lastFromCache },
      );
    }, onError);
  }, [studio, buildQuery, attempt]);

  const stored = useStoredCollection({
    uid: studio, collection, subscribe, path: readPath, enabled: viaStore,
  });

  // `{ uid, rows }` rather than a bare array, because "have we read *this*
  // studio's window" is a different question from "has loading finished" and
  // several providers turn on it — see `hasRead` below.
  const [snapshot, setSnapshot] = useState(null);
  // The snapshot path's listener error (#647), for the studio it was about.
  const [snapshotError, setSnapshotError] = useState(null);

  useEffect(() => {
    setSnapshotError(null);
    // Inert when the store is serving this session. Left in place rather than
    // deleted so the flag can go back off in a browser without a rebuild; the
    // two paths are exclusive, so there is never a moment with two listeners
    // on one query.
    if (viaStore) return undefined;
    if (!studio) {
      setSnapshot(null);
      return undefined;
    }
    // Whatever is in hand belongs to whoever was signed in a moment ago.
    setSnapshot(null);
    // Always with metadata changes (#651), and filtered back to what this
    // provider asked for unless it asked for them — see `deliverable`.
    let lastFromCache = null;
    // The server answered this listener: kept here as well as in
    // `serverAnswers`, whose `localStorage` a private window may refuse.
    let heardServer = false;
    const handle = (snap) => {
      if (!withMetadata && !deliverable(lastFromCache, snap)) return;
      lastFromCache = cached(snap);
      if (!lastFromCache) {
        heardServer = true;
        rememberServerAnswered(studio, collection);
      }
      setSnapshot({
        uid: studio,
        // Whether the server has answered this listener in this session — the
        // snapshot path's `fresh` (#682), which `reportRead` below turns into
        // the chip's stale count as the store path's receipt state is.
        heard: heardServer,
        // Whether the server has answered this list on this browser — this
        // snapshot, an earlier one on this listener, or an earlier session
        // whose SDK cache is still the one answering (#651).
        confirmed: heardServer || serverAnsweredBefore(studio, collection),
        rows: snap.docs.map((d) => ({
          id: d.id,
          data: d.data(),
          pendingWrite: d.metadata.hasPendingWrites,
          // The snapshot path cannot answer #268 — `partial` is about *our*
          // queue folding onto a base this device may not have, and on this
          // path there is no fold. What the SDK hands over is a whole document.
          partial: false,
        })),
      });
    };
    const failure = (err) => {
      console.error(`[reads] listening to ${collection} failed:`, err);
      setSnapshotError({ uid: studio, err });
    };
    return onSnapshot(buildQuery(), { includeMetadataChanges: true }, handle, failure);
  }, [viaStore, studio, buildQuery, withMetadata, collection, attempt]);

  // ── A read served without a fresh snapshot says so (#207 U6) ────────────
  //
  // `readState` is reported to the sync chip's stale count here, for every
  // provider at once.
  //
  // **Both paths report (#682).** Every listener here hears metadata changes
  // (#651), so `onSnapshot` is asked whether it has spoken to a server —
  // `metadata.fromCache` — and a listener that has answered only from the
  // SDK's own cache this session is `stale` in exactly the store path's sense:
  // shown from this device, with nothing from the server behind it yet.
  // Without this, a cold start in this arm would say "Up to date" over the
  // SDK's cache with the server unreachable.
  const readFor = snapshot && snapshot.uid === studio ? snapshot : null;
  const snapshotState = readFor ? (readFor.heard ? READ_STATE.fresh : READ_STATE.stale) : null;
  const readState = viaStore ? stored.state : snapshotState;
  useEffect(() => {
    if (!studio) return undefined;
    const scope = collectionScope(studio, collection);
    reportRead(scope, readState);
    return () => forgetRead(scope);
  }, [studio, collection, readState]);

  const rows = viaStore ? stored.docs : (readFor ? readFor.rows : EMPTY);
  const docs = useMemo(() => rows.map(normalise), [rows, normalise]);

  /**
   * Have we actually read **this studio's** window?
   *
   * Not the same question as `loading`, and several providers turn on it: a
   * write issued before the first read is a write against a collection this app
   * has not seen, and in `useCategories` that would re-create built-in
   * categories a studio had deleted.
   *
   * On the store path this is *we hold a receipt for this scope* — `stale` or
   * `fresh`, never `loading` and never the `null` that means the first read has
   * not come back. The uid scoping the snapshot path needs a `readFor` for is
   * free here: `appStore` keys every receipt by `users/{uid}/…`, so a receipt
   * for another teacher's studio is a receipt at another scope.
   *
   * **And it is the answer offline as well**, which the snapshot path cannot
   * give. `stale` means the last window we saw for this studio was this one, so
   * a cold offline start can write against it.
   */
  const hasRead = viaStore
    ? (stored.state === READ_STATE.stale || stored.state === READ_STATE.fresh)
    : readFor !== null;

  // **An answer that is not a read (#651).** The window has answered, from a
  // cache that has never had the server's answer for this list on this
  // device, and it holds nothing: a device that never synced, most likely
  // offline. That is not an empty studio, so the window stays `loading` —
  // every empty state and figure waits, as it does for an unread list — and
  // `awaiting` says why, which `Unread` turns into the core's sentence.
  // `core/src/reading.rs` has the rule; this is where the web asks it.
  const answer = viaStore
    ? (stored.state === READ_STATE.stale || stored.state === READ_STATE.fresh
      ? { confirmed: stored.confirmed !== false, count: stored.docs.length }
      : null)
    : (readFor ? { confirmed: readFor.confirmed, count: readFor.rows.length } : null);
  const awaiting = Boolean(studio) && answer !== null
    && !answerIsRead(answer.confirmed, answer.count);

  // **Signed out is not loading**: there is nothing to read, and an empty
  // list is the answer. Spelled here because `useStoredCollection` with no uid
  // holds no subscription and never produces a first read, so its own
  // `loading` stays true.
  const loading = Boolean(studio) && ((viaStore ? stored.loading : !hasRead) || awaiting);

  // **The read stopped before it answered (#647).** Only while `loading`: a
  // window that answered and then lost its listener is what it answered,
  // one snapshot behind, as anything read from a cache is. Otherwise this
  // would be `loading` for good, and each screen's loading state never leave.
  const failed = loading && (viaStore
    ? Boolean(stored.failed)
    : Boolean(snapshotError && snapshotError.uid === studio));
  failedRef.current = failed;

  // Tell the loading state (#651). Keyed per mounted window rather than per
  // scope, so two windows on one collection cannot clear each other's report.
  // A failed read is not waiting: its own state says so.
  const [serial] = useState(() => { windowSerial += 1; return windowSerial; });
  const waiting = awaiting && !failed;
  useEffect(() => {
    if (!studio) return undefined;
    const key = `${collectionScope(studio, collection)}#${serial}`;
    reportAwaiting(key, waiting);
    return () => forgetAwaiting(key);
  }, [studio, collection, serial, waiting]);

  return {
    docs,
    hasRead,
    // The other question, by name (#279). Not `hasRead`: a studio that queued a
    // creation this session with nothing stored has something to show and no
    // receipt, and `useCategories` must keep writing against the receipt while
    // a list happily renders the row.
    hasAnswer: Boolean(studio) && !loading,
    loading,
    failed,
    // The window answered, from a cache the server has never confirmed, with
    // nothing in it (#651) — `loading` is true through it.
    awaiting: waiting,
    readVia: viaStore ? 'store' : 'snapshot',
    readState: viaStore ? stored.state : null,
    readSeenAt: viaStore ? stored.seenAt : null,
    complete: viaStore ? stored.complete : true,
    refusals: viaStore ? stored.refusals : [],
    storeRefusals: viaStore ? stored.storeRefusals : [],
    error: viaStore ? stored.error : null,
  };
}
